import { AxiosResponse } from 'axios'
import get from 'lodash/get'
import { IAdminChangePasswordDTO, IChangePasswordDTO } from 'types/auth'
import { IFilter, PaginationList, Where } from 'types/common'
import { AggregationPipeline } from 'types/common/aggregation'
import { api } from 'API'
import { IGroupMember } from 'interfaces/group'
import { IProcessFilter, IProcessWithRelations } from 'interfaces/process'
import {
  IAuditWithRelations,
  ICreateEditUserRequest,
  IEditEditUserRequest,
  IUpdateUserPermission,
  IUser,
  IUserWithRelations
} from 'interfaces/user'
import { BackendSuccessResponse } from 'interfaces/common'

export async function createUser(user: ICreateEditUserRequest): Promise<IUser> {
  const createdUser: AxiosResponse = await api.post('/users', user)
  return createdUser.data
}

export async function getUsers(filter: IFilter<IUser> = {}): Promise<IUser[]> {
  const user: AxiosResponse = await api.get('/users', { params: { filter } })
  return user.data
}

export async function getUsersAggregate(pipeline: AggregationPipeline = []): Promise<PaginationList<IUser>> {
  const user: AxiosResponse = await api.post('/users/aggregate', { pipeline })
  return user.data
}

export async function countUsers(where: Where<IUser> = {}): Promise<number> {
  const user: AxiosResponse = await api.get('/users/count', { params: { where } })
  return get(user, 'data.count', 0)
}

export async function countUsersAuth(where: Where<IUser> = {}): Promise<number> {
  const user: AxiosResponse = await api.get('/auth/count', { params: { where } })
  return get(user, 'data.count', 0)
}

export async function getUserById(userId: number, filter: IFilter<IUser> = {}): Promise<IUserWithRelations> {
  const user: AxiosResponse = await api.get(`/users/${userId}?filter=${JSON.stringify(filter)}`)
  return user.data
}

export async function updateUserById(userId: number, userData: IEditEditUserRequest = {}): Promise<void> {
  await api.patch(`/users/${userId}`, userData)
}

export async function updateUserAuthMethod(
  userId: number, 
  data: { authMethodOverride?: 'password' | 'sso' | null; forcePasswordChangeOnNextLogin?: boolean }
): Promise<void> {
  await api.patch(`/admin/users/${userId}/auth-method`, data)
}

export async function addFcmToken(userId: number, fcmToken: string): Promise<void> {
  await api.post(`/users/${userId}/fcm-tokens`, { fcmToken })
}

export async function validatePassword(userId: number, password: string): Promise<boolean> {
  const result = await api.post(`/users/${userId}/validate-password`, { password })
  return result.data
}

export async function changePassword(data: IChangePasswordDTO): Promise<boolean> {
  const result = await api.post('/users/change-password', data)
  return result.data
}

export async function adminChangePassword(data: IAdminChangePasswordDTO): Promise<boolean> {
  const result = await api.post('/admin/users/change-password', data)
  return result.data
}

export async function getRecentlyViewed(limit: number = 10): Promise<IAuditWithRelations[]> {
  const response: AxiosResponse = await api.get(`/users/me/recently-viewed?limit=${limit}`)
  return response.data
}

export async function getUserPermissionsOnProcesses(
  userId: number,
  processIds: number[]
): Promise<{ userPermissions: IGroupMember[]; id: number }[]> {
  const response: AxiosResponse = await api.post(`/users/${userId}/processes/permissions`, { processIds })
  return response.data
}

export async function getUserPermissionsOnCollections(
  userId: number,
  collectionIds: number[]
): Promise<{ userPermissions: IGroupMember[]; id: number }[]> {
  const response: AxiosResponse = await api.post(`/users/${userId}/collections/permissions`, { collectionIds })
  return response.data
}

export async function shareCollections(userId: number, collectionIds: number[]): Promise<void> {
  await api.post(`/users/${userId}/share-collections`, collectionIds)
}

export async function unshareCollection(userId: number, collectionId: number): Promise<void> {
  await api.post(`/users/${userId}/unshare-collection`, collectionId)
}

export async function getUserDetailProcess(filter: IProcessFilter): Promise<PaginationList<IProcessWithRelations>> {
  const response: AxiosResponse = await api.post('/users/processes', filter)
  return response.data
}

export async function getShareProcess(
  filter: Pick<IProcessFilter, 'groupIds' | 'userId' | 'organizationId'>
): Promise<IProcessWithRelations[]> {
  const response: AxiosResponse = await api.post('/users/processes/share', filter)
  return response.data
}

export async function shareProcesses(userId: number, processIds: number[]): Promise<void> {
  await api.post(`/users/${userId}/share-processes`, processIds)
}

export async function unshareProcesses(userId: number, processId: number): Promise<void> {
  await api.post(`/users/${userId}/unshare-processes`, processId)
}

export async function updateUserPermissionById(userId: number, data: IUpdateUserPermission): Promise<void> {
  await api.patch(`/users/${userId}/permission`, data)
}

export async function canUserCreateCollections(userId: number): Promise<boolean> {
  const response: AxiosResponse = await api.get(`/users/${userId}/can-create-collections`)
  return response.data
}

export interface IUserConflictResult {
  type: 'none' | 'active' | 'inactive'
  userId: number | null
}

export async function checkUserConflict(
  field: 'email' | 'username',
  value: string,
  organizationId: number
): Promise<IUserConflictResult> {
  const result: AxiosResponse = await api.get('/users/conflict-check', {
    params: { field, value, organizationId }
  })
  return result.data
}

export async function purgeDeletedUser(userId: number): Promise<void> {
  await api.delete(`/admin/users/${userId}/purge`)
}

export async function renameDeletedUser(userId: number): Promise<{ newEmail: string; newUsername: string }> {
  const result: AxiosResponse = await api.patch(`/admin/users/${userId}/rename-deleted`)
  return result.data
}

// Admin SSO management APIs
export async function resetSsoEmail(userId: number): Promise<{ message: string }> {
  const response = await api.post(`/admin/users/${userId}/reset-email`)
  return response.data
}

// Email verification APIs
export async function bindWorkEmail(workEmail: string): Promise<BackendSuccessResponse> {
  const response = await api.post('/email/bind-email', { ssoEmail: workEmail })
  return response.data
}

export async function verifyWorkEmail(token: string): Promise<BackendSuccessResponse> {
  const response = await api.post('/email/verify-email', { token })
  return response.data
}

export async function resendWorkEmailVerification(): Promise<BackendSuccessResponse> {
  const response = await api.post('/email/resend-email-verification')
  return response.data
}
